<script lang="ts" setup>
import type { RouteLocationNormalized } from 'vue-router'
import type { Player, PlayerRoster, Team } from '~/types'

interface Props {
  team: Team.Model
}

defineProps<Props>()

definePageMeta({
  layout: false,
  validate: (route: RouteLocationNormalized) => routeValidate(route),
})

const rosterId = useRouteQuery('rosterId', null, { transform: (val: string | null) => val ? Number(val) : null })

const currentEntity = useCurrentEntity()

const { params } = useRoute()

const slug = computed<string>(() => params?.player.toString())
const paramsSearch: Player.SearchRequest = {
  include: ['photo', 'playerRosters.photo'],
}

const { errorObject } = useShowApiError('Player Not Found')
const { fetchData } = useLoadPlayerBySlug(slug.value, paramsSearch)
const { data, error } = await useAsyncData(`player-${slug.value}`, () => fetchData())

if (error.value) {
  throw errorObject
}

const playerRoster = computed<Maybe<PlayerRoster.Model>>(() => {
  if (!rosterId.value || !data.value) {
    return null
  }

  return data.value?.playerRosters?.find(playerRoster => playerRoster.rosterId === rosterId.value) ?? null
})

const {
  seoMetaInput,
  headInput,
  player,
  id,
  fullName,
} = usePlayer(data)

currentEntity.value = {
  title: `Player ${fullName.value}`,
  dashboardUrl: `resources/players/${id.value}`,
  type: 'rosterPlayer',
}

useSeoMeta(seoMetaInput.value)
useHead(headInput.value)
</script>

<template>
  <Player
    v-if="player"
    :player
    :player-roster
    class="player-page"
  >
    <div class="player-page__nav">
      <Wrapper>
        <PlayerNav
          :player
          :team
        />
      </Wrapper>
    </div>
    <NuxtPage
      :player
      :player-roster
      :team
    />
  </Player>
</template>

<style lang="scss" scoped>
.player-page {
  @apply mb-10;

  &__nav {
    @apply mb-10 border-b border-b-primary/20;
    @apply lg:mb-[50px];
  }
}
</style>
